export const CURRENCY_SYMBOLS = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  SEK: "kr",
  // Add more currencies as needed
};

export const getCurrencySymbol = (currencyCode) => {
  return CURRENCY_SYMBOLS[currencyCode] || "$";
};

/**
 * Helper to parse money values in API format: {amount: 100, formatted: "$100", currency: "USD"}
 *
 * @param {*} moneyField - The money field to parse
 * @returns {{value: number, currency: string|null}} Parsed value and currency
 */
export const parseMoneyValue = (moneyField) => {
  if (!moneyField) {
    return { value: 0, currency: null };
  }

  return {
    value: parseFloat(moneyField.amount) || 0,
    currency: moneyField.currency || null,
  };
};

/**
 * Get formatted currency string from API money object.
 * Always uses our formatCurrency to ensure consistent rounding (up) and no cents.
 *
 * @param {*} moneyField - The money field from API: {amount: 100, formatted: "$100", currency: "USD"}
 * @param {string} defaultCurrency - Default currency code if none provided
 * @returns {string} Formatted currency string (e.g., "$100" or "-€525")
 */
export const getFormattedMoney = (moneyField, defaultCurrency = "EUR") => {
  if (!moneyField) {
    return formatCurrency(0, defaultCurrency);
  }

  // Always use our formatCurrency to ensure rounding and no cents
  const amount = parseFloat(moneyField.amount) || 0;
  const currency = moneyField.currency || defaultCurrency;
  return formatCurrency(amount, currency);
};

/**
 * Format currency value with consistent rounding (up) and no cents.
 *
 * @param {number|string} value - The value to format
 * @param {string} currency - Currency code (USD, EUR, GBP)
 * @param {Object} options - Formatting options
 * @param {boolean} options.abbreviated - Use K/M suffixes for large values (default: true)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, currency, options = {}) => {
  const { abbreviated = true } = options;
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  if (!value) return `${symbol}0`;

  const numValue = typeof value === "string" ? parseFloat(value) : value;
  const isNegative = numValue < 0;
  const absValue = Math.abs(numValue);
  // Round up (ceiling) - for negative numbers, ceiling moves toward zero
  const roundedValue = Math.ceil(absValue);
  const prefix = isNegative ? "-" : "";

  if (abbreviated) {
    if (roundedValue >= 1000000) {
      return `${prefix}${symbol}${(roundedValue / 1000000).toFixed(1)}M`;
    } else if (roundedValue >= 1000) {
      return `${prefix}${symbol}${(roundedValue / 1000).toFixed(1)}K`;
    }
  }

  return `${prefix}${symbol}${roundedValue.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
};
